import { useState } from "react";
import { Download, FileText } from "lucide-react";
import {
  formatMultiplier,
  fromMultiplier,
  LIME_MULTIPLIER_MAX,
  LIME_MULTIPLIER_MIN,
  LIME_MULTIPLIER_STEP,
} from "../../lib/limeMultiplier";
import { BASIS_LABEL, type BlendBasis } from "../../lib/spreadBlend";
import type { SpreadRxNutrient } from "../../lib/spreadRx";

export function efficiencyLabel(nutrient: SpreadRxNutrient): string {
  return nutrient === "lime"
    ? "Multiplier (relative to 90% CCE lime, the recs default)"
    : nutrient === "p2o5"
      ? "% P₂O₅"
      : "% K₂O";
}

function LimeMultiplierField({
  pct,
  onPct,
}: {
  pct: number;
  onPct: (efficiencyPct: number) => void;
}) {
  const label = efficiencyLabel("lime");
  // Draft exists only while the box is focused, so a stored percent can render as a multiplier
  // without rewriting the digits the person is still typing.
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? formatMultiplier(pct);
  const multiplier = Number(text);
  const bad =
    text.trim() !== "" &&
    (!Number.isFinite(multiplier) ||
      multiplier < LIME_MULTIPLIER_MIN ||
      multiplier > LIME_MULTIPLIER_MAX);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-muted">{label}</span>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          min={LIME_MULTIPLIER_MIN}
          max={LIME_MULTIPLIER_MAX}
          step={LIME_MULTIPLIER_STEP}
          value={text}
          onFocus={() => setDraft(text)}
          onBlur={() => setDraft(null)}
          onChange={(e) => {
            const raw = e.target.value;
            setDraft(raw);
            const next = Number(raw);
            if (
              Number.isFinite(next) &&
              next >= LIME_MULTIPLIER_MIN &&
              next <= LIME_MULTIPLIER_MAX
            ) {
              onPct(fromMultiplier(next));
            }
          }}
          aria-label={label}
          className={`tnum w-20 rounded border bg-canvas px-1.5 py-1 text-fg outline-none focus:border-accent ${bad ? "border-red-500" : "border-line"}`}
        />
        <span className="text-faint">×</span>
      </div>
      {bad ? (
        <span className="text-[10px] leading-snug text-red-500">
          Enter a multiplier from 0.50 to 3.00.
        </span>
      ) : (
        <span className="text-[10px] leading-snug text-faint">
          1.00× spreads the Rx. The Rx is tons of 90% CCE lime.
        </span>
      )}
    </label>
  );
}

function pureUnit(nutrient: SpreadRxNutrient): string {
  return nutrient === "lime"
    ? "tons of 90% CCE lime"
    : nutrient === "p2o5"
      ? "lbs of pure P₂O₅"
      : "lbs of pure K₂O";
}

export function SpreadProductRates({
  exports,
}: {
  exports: { fieldId: string; fieldName: string; avg: number | null }[];
}) {
  return (
    <div className="rounded border border-line bg-canvas/40 p-2">
      {/* the resulting product rate, per field — the number that lands in the file */}
      <h3 className="mb-1 text-[11px] font-semibold text-muted">
        Product rate
      </h3>
      {exports.length === 0 ? (
        <span className="text-[11px] text-faint">Nothing to spread yet.</span>
      ) : (
        <ul className="space-y-0.5 text-[11px]">
          {exports.map((e) => (
            <li key={e.fieldId} className="flex justify-between gap-2">
              <span className="truncate text-fg">{e.fieldName}</span>
              <span className="shrink-0 tabular-nums text-muted">
                {e.avg != null ? `${Math.round(e.avg)} lbs/ac avg` : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SpreadEfficiency({
  nutrients,
  pct,
  onPct,
}: {
  nutrients: SpreadRxNutrient[];
  pct: Record<string, number>;
  onPct: (nutrient: SpreadRxNutrient, value: number) => void;
}) {
  return (
    <>
      {/* ONE efficiency box per selected nutrient — a blend has an analysis for each. */}
      {nutrients.map((n) =>
        n === "lime" ? (
          <LimeMultiplierField
            key={n}
            pct={pct[n] ?? 90}
            onPct={(value) => onPct(n, value)}
          />
        ) : (
          <label key={n} className="flex flex-col gap-1">
            <span className="text-[11px] font-medium text-muted">
              {efficiencyLabel(n)}
            </span>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={1}
                max={200}
                step={1}
                value={Number.isFinite(pct[n]) ? pct[n] : ""}
                onChange={(e) => onPct(n, e.target.valueAsNumber)}
                aria-label={efficiencyLabel(n)}
                className="tnum w-20 rounded border border-line bg-canvas px-1.5 py-1 text-fg outline-none focus:border-accent"
              />
              <span className="text-faint">%</span>
            </div>
            <span className="text-[10px] leading-snug text-faint">
              That Rx is {pureUnit(n)}. Its rate is that ÷{" "}
              {(pct[n] ?? 0) > 0 ? pct[n] : 100}%.
            </span>
          </label>
        ),
      )}
    </>
  );
}

function SpreadBasis({
  basis,
  options,
  onBasis,
}: {
  basis: BlendBasis;
  options: BlendBasis[];
  onBasis: (basis: BlendBasis) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-muted">Spread to</span>
      <select
        value={basis}
        aria-label="Spread to"
        onChange={(e) => onBasis(e.target.value as BlendBasis)}
        className="rounded border border-line bg-canvas px-1.5 py-1 text-fg outline-none focus:border-accent"
      >
        {options.map((b) => (
          <option key={b} value={b}>
            {BASIS_LABEL[b]}
          </option>
        ))}
      </select>
      <span className="text-[10px] leading-snug text-faint">
        {basis === "max"
          ? "Every acre gets at least what each selected nutrient calls for — the others land above their target."
          : basis === "avg"
            ? "The average of the selected nutrients’ rates, so some acres land under one of them."
            : `Spreads to ${BASIS_LABEL[basis]}; the other nutrients land wherever that rate puts them.`}
      </span>
    </label>
  );
}

export function SpreadEditorSide(props: {
  nutrients: SpreadRxNutrient[];
  pct: Record<string, number>;
  onPct: (nutrient: SpreadRxNutrient, value: number) => void;
  pctOk: boolean;
  basis: BlendBasis;
  basisOptions: BlendBasis[];
  onBasis: (basis: BlendBasis) => void;
  column: string;
  readyCount: number;
  fieldCount: number;
  downloadError: string | null;
  canDownload: boolean;
  onDownload: () => void;
  onDownloadPdf: () => void;
  send: React.ReactNode;
}) {
  return (
    <div className="flex w-full shrink-0 flex-col gap-3 border-t border-line p-3 text-[12px] sm:w-64 sm:border-l sm:border-t-0">
      <SpreadEfficiency
        nutrients={props.nutrients}
        pct={props.pct}
        onPct={props.onPct}
      />
      {!props.pctOk && (
        <span className="text-[10px] text-amber-500">
          Every percentage must be above 0.
        </span>
      )}
      {/* THE BLEND BASIS — only a question when there is more than one nutrient to reconcile. */}
      {props.nutrients.length > 1 && (
        <SpreadBasis
          basis={props.basis}
          options={props.basisOptions}
          onBasis={props.onBasis}
        />
      )}
      <div className="rounded border border-line bg-canvas/40 p-2 text-[11px] text-muted">
        <span className="block text-fg">Column: {props.column}</span>
        <span className="block text-faint">
          {props.readyCount} of {props.fieldCount} field
          {props.fieldCount === 1 ? "" : "s"} ready
        </span>
      </div>
      <div className="mt-auto flex flex-col gap-2">
        {props.downloadError && (
          <p className="text-red-400">{props.downloadError}</p>
        )}
        <button
          onClick={props.onDownload}
          disabled={!props.canDownload}
          className="flex items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-40"
        >
          <Download size={13} /> Download spread map
        </button>
        {/*
          Beside the spread map, not instead of it: the ZIP is what a controller eats, this is what a
          person reads. Both are built from the SAME `exports`, so they cannot describe different rates.
        */}
        <button
          onClick={props.onDownloadPdf}
          disabled={!props.canDownload}
          data-wizard-anchor="spread-download-pdf"
          className="flex items-center justify-center gap-1.5 rounded border border-line px-3 py-1.5 text-[12px] font-medium text-fg hover:bg-hover disabled:opacity-40"
        >
          <FileText size={13} /> Download PDF report
        </button>
        {/* The SAME `exports` the zip and PDF are built from: the file sent is byte-for-byte the file the Download button gives. */}
        {props.send}
      </div>
    </div>
  );
}
